/**
 * Reverse-geocoding helper — Nominatim (OpenStreetMap).
 * Public API, no key, fair-use policy (≤1 req/s, include User-Agent recommended).
 *
 * Returns { address, postal_code, city, country, display_name } or null on failure.
 * `address` is the street address (e.g., "12 rue de Rivoli").
 */
export async function reverseGeocode(lat, lng, { signal } = {}) {
  if (lat == null || lng == null) return null;
  const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`;
  try {
    const r = await fetch(url, {
      headers: { Accept: "application/json" },
      signal,
    });
    if (!r.ok) return null;
    const d = await r.json();
    const a = d.address || {};
    const houseNumber = a.house_number || "";
    const road = a.road || a.pedestrian || a.footway || a.path || "";
    const street = [houseNumber, road].filter(Boolean).join(" ").trim();
    const city = a.city || a.town || a.village || a.municipality || a.suburb || "";
    const postal = a.postcode || "";
    return {
      address: street,
      postal_code: postal,
      city,
      country: a.country || "",
      display_name: d.display_name || "",
    };
  } catch {
    return null;
  }
}

/**
 * Try to extract postal_code + city from a raw French address string.
 * Matches "12 rue X, 75001 Paris" or "12 rue X 75001 Paris".
 * Returns { street, postal_code, city } — empty strings on no match.
 */
export function parseFrenchAddress(raw = "") {
  const src = String(raw).trim();
  if (!src) return { street: "", postal_code: "", city: "" };
  // 5-digit postal code followed by city
  const m = src.match(/^(.*?)[\s,]+(\d{5})\s+([^,]+?)\s*$/);
  if (m) {
    return {
      street: m[1].replace(/[,\s]+$/, "").trim(),
      postal_code: m[2],
      city: m[3].trim(),
    };
  }
  return { street: src, postal_code: "", city: "" };
}
